import { triggerAuthError } from '../auth/authErrorHandler';
import { createApiError, createServerError, fetchWithNetworkRetry, formatHttpErrorWithDebug, SERVER_ERROR_MESSAGE } from './apiError';
import { clearItemsCache } from './itemsApi';
import { invalidateAllCachesUsedByFetchMeta } from './tableData';
import { clearRelationLabelCache } from '../utils/relationLabelCacheStore';
import { resolveApiBaseUrl } from './apiBase';
import { clearImpersonationStorage } from '../auth/impersonationStorage';

const API_BASE_URL = resolveApiBaseUrl();

export const DEMO_ACCOUNT_EMAIL = 'demo@labwick.com';

export function isDemoAccountEmail(email: string | null | undefined): boolean {
    return (email || '').trim().toLowerCase() === DEMO_ACCOUNT_EMAIL;
}

export type UserAccountRole = 'admin' | 'user';

export interface User {
    uid: string;
    id?: string;
    _id?: string;
    email: string;
    active?: boolean;
    admin?: boolean;
    /** Canonical account type: admin or user (legacy member/visitor/manager normalize to user). */
    role?: UserAccountRole | string;
    firstName?: string;
    lastName?: string;
    organization?: string;
    collectionLimit?: number | null;
    collectionCount?: number;
    /** Remembered default Labwick sharing scope for newly uploaded files. */
    fileVisibilityDefault?: 'private' | 'shared';
    /** Remembered default ACL group for shared uploads. */
    fileShareGroupDefault?: string | null;
    /** Remembered default public serve gate for newly uploaded files. */
    filePublicDefault?: boolean;
    /** Dashboard mode. Absent values are submitter. */
    userType?: 'submitter' | 'website_owner';
    /** Interface hide flags. Absent or non-true values keep the section visible. */
    uiSettings?: {
        hideInbox?: boolean;
        hideActions?: boolean;
        hideSubmissions?: boolean;
    } | null;
    /** Per-table column order and hidden columns, stored on the account. */
    columnViews?: Array<{
        tableKey: string;
        order: string[];
        hidden: string[];
    }> | null;
    _created?: Date;
    _updated?: Date;
    info?: {
        firstName?: string;
        lastName?: string;
        organization?: string;
        active: boolean;
        admin?: boolean;
        role?: string;
        name?: string;
        id?: string;
        _id?: string;
    };
}

export interface AuthResponse {
    success: boolean;
    token?: string;
    idToken?: string;
    refreshToken?: string;
    user?: User;
    message?: string;
}

class AuthAPI {
    private isBrowserEnvironment = (): boolean => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

    private getAuthToken = (): string | null => {
        if (!this.isBrowserEnvironment()) {
            return null;
        }

        const storages = [localStorage, sessionStorage];
        for (const storage of storages) {
            const authToken = storage.getItem('authToken');
            if (authToken && authToken.trim()) {
                return authToken.trim();
            }
        }
        for (const storage of storages) {
            const legacyToken = storage.getItem('idToken');
            if (legacyToken && legacyToken.trim()) {
                return legacyToken.trim();
            }
        }
        return null;
    };

    private setAuthToken = (token: string): void => {
        try {
            if (!this.isBrowserEnvironment()) {
                throw new Error('Storage is not available in this environment');
            }
            // Trim token before saving to avoid whitespace issues
            const trimmedToken = token.trim();
            // Store in both keys during migration period, then we can remove 'idToken'
            localStorage.setItem('authToken', trimmedToken);
            localStorage.setItem('idToken', trimmedToken);
            try {
                sessionStorage.setItem('authToken', trimmedToken);
                sessionStorage.setItem('idToken', trimmedToken);
            } catch (sessionError) {
                console.warn('⚠️ AuthAPI: Unable to persist token in sessionStorage:', sessionError);
            }
            // Verify the token was actually saved
            const savedToken = localStorage.getItem('authToken');
            if (!savedToken || savedToken !== trimmedToken) {
                console.error('❌ AuthAPI: Token was not saved correctly to localStorage');
                throw new Error('Failed to save authentication token');
            }
            // Ensure stale list/relation cache does not survive auth changes.
            clearItemsCache();
            clearRelationLabelCache();
            console.log('✅ AuthAPI: Token saved successfully to localStorage');
        } catch (error) {
            console.error('❌ AuthAPI: Error saving token to localStorage:', error);
            throw error;
        }
    };

    private clearAuthToken = (): void => {
        if (!this.isBrowserEnvironment()) {
            return;
        }
        localStorage.removeItem('authToken');
        localStorage.removeItem('idToken');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('idToken');
        localStorage.removeItem('user');
        clearImpersonationStorage();
        // Ensure stale list/relation cache does not survive auth changes.
        clearItemsCache();
        clearRelationLabelCache();
    };

    private persistLoginSession(data: AuthResponse): User {
        const jwtToken = data.token || data.idToken;
        if (!jwtToken || !data.user) {
            throw new Error('Invalid response from server');
        }

        this.setAuthToken(jwtToken);
        localStorage.setItem('user', JSON.stringify(data.user));
        // A real login is not impersonation; drop any leftover admin restore keys.
        clearImpersonationStorage();

        if (!data.user.active && !data.user.info?.active) {
            this.clearAuthToken();
            throw new Error('Your account is inactive. Please contact an administrator to activate your account.');
        }

        return data.user;
    }

    private makeRequest = async (endpoint: string, options: RequestInit = {}, clearOn401: boolean = true): Promise<any> => {
        const token = this.getAuthToken();

        const config: RequestInit = {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
                ...options.headers,
            },
        };

        const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

        if (response.status === 401) {
            // Token expired or invalid
            // Only clear if explicitly requested (default true for backward compatibility)
            if (clearOn401) {
                this.clearAuthToken();
                triggerAuthError();
            }
            throw new Error('Authentication required. Please login again.');
        }

        if (!response.ok) {
            if (response.status >= 500) {
                throw createServerError(response.status);
            }
            const errorData = await response.json().catch(() => ({ message: 'Network error' }));
            const errorMessage = errorData.message || `HTTP ${response.status}`;
            throw createApiError(errorMessage, response.status);
        }

        return await response.json();
    };

    // Initiate login (step 1: email -> magic link email)
    async loginInitiate(
        email: string,
        options?: {
            source?: string;
            returnPath?: string;
            draft?: { token?: string; data?: Record<string, any>; clientSessionId?: string };
            tenantAbbreviation?: string;
        }
    ): Promise<{ success: boolean; message: string }> {
        const loginInitiateUrl = `${API_BASE_URL}/auth/login/initiate`;
        try {
            console.log('🔐 AuthAPI: Initiating login...');
            const normalizedEmail = email.trim().toLowerCase();
            if (!normalizedEmail) {
                throw new Error('Email is required');
            }
            const response = await fetchWithNetworkRetry(loginInitiateUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: normalizedEmail,
                    source: options?.source,
                    returnPath: options?.returnPath,
                    draft: options?.draft,
                    tenantAbbreviation: options?.tenantAbbreviation,
                }),
            });

            const responseText = await response.text();
            let data: AuthResponse | null = null;
            try {
                data = responseText ? JSON.parse(responseText) : null;
            } catch (parseError) {
                console.error('❌ AuthAPI: Failed to parse login initiate response:', parseError);
                if (!response.ok) {
                    const retryAfter = response.status === 429
                        ? (parseInt(response.headers.get('Retry-After') || '60', 10) || 60)
                        : undefined;
                    const errorMessage = response.status === 429
                        ? 'Too many requests. Please wait a moment and try again.'
                        : `Login initiation failed: ${responseText}`;
                    throw createApiError(errorMessage, response.status, retryAfter);
                }
                throw new Error(`Invalid JSON response: ${responseText}`);
            }

            if (!response.ok || !data?.success) {
                if (response.status >= 500) {
                    throw createApiError(
                        formatHttpErrorWithDebug(SERVER_ERROR_MESSAGE, loginInitiateUrl, {
                            'HTTP status': response.status,
                            Response: data?.message || responseText.slice(0, 200) || undefined,
                        }),
                        response.status
                    );
                }
                const retryAfter = response.status === 429
                    ? (parseInt(response.headers.get('Retry-After') || '60', 10) || 60)
                    : undefined;
                const fallbackMessage = response.status === 404
                    ? 'User not found'
                    : response.status === 400
                        ? 'Please enter a valid email address'
                        : response.status === 429
                            ? 'Too many requests. Please wait a moment and try again.'
                            : 'Login initiation failed';
                throw createApiError(data?.message || fallbackMessage, response.status, retryAfter);
            }

            console.log('AuthAPI: Login initiation successful');
            return { success: true, message: data.message || 'Check your email for a login link' };
        } catch (error: unknown) {
            console.error('AuthAPI: Login initiation error:', error);
            throw error;
        }
    }

    async emailExists(email: string): Promise<{ exists: boolean }> {
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail) {
            throw new Error('Email is required');
        }
        const response = await fetch(`${API_BASE_URL}/auth/email/exists`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: normalizedEmail }),
        });
        const responseText = await response.text();
        if (!response.ok) {
            throw createApiError(responseText || 'Failed to check email', response.status);
        }
        const data = JSON.parse(responseText);
        if (!data.success) {
            throw createApiError(data.message || 'Failed to check email', response.status);
        }
        return { exists: !!data.exists };
    }

    async publicFormAuthInitiate(
        email: string,
        options?: {
            returnPath?: string;
            draft?: { token?: string; data?: Record<string, any>; clientSessionId?: string };
            tenantAbbreviation?: string;
        }
    ): Promise<{ success: boolean; message: string; linkType?: 'login' | 'registration' }> {
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail) {
            throw new Error('Email is required');
        }
        const response = await fetch(`${API_BASE_URL}/auth/public-form/initiate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                email: normalizedEmail,
                returnPath: options?.returnPath,
                draft: options?.draft,
                tenantAbbreviation: options?.tenantAbbreviation,
            })
        });
        const responseText = await response.text();
        if (!response.ok) {
            const retryAfter = response.status === 429
                ? (parseInt(response.headers.get('Retry-After') || '60', 10) || 60)
                : undefined;
            let msg = responseText || 'Failed to send link';
            try {
                const parsed = responseText ? JSON.parse(responseText) : null;
                if (parsed && typeof parsed.message === 'string') msg = parsed.message;
            } catch {
                // use msg as-is
            }
            throw createApiError(msg, response.status, retryAfter);
        }
        const data = JSON.parse(responseText);
        if (!data.success) {
            throw createApiError(data.message || 'Failed to send link', response.status);
        }
        return {
            success: true,
            message: data.message || 'Link sent to your email',
            linkType: data.linkType
        };
    }

    // Verify login magic link (step 2: token -> JWT)
    async loginVerify(token: string): Promise<User> {
        try {
            console.log('🔐 AuthAPI: Verifying login token...');
            const sanitizedToken = token.trim();
            if (!sanitizedToken) {
                throw new Error('Invalid login token');
            }

            const response = await fetch(`${API_BASE_URL}/auth/login/verify`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: sanitizedToken }),
            });

            const responseText = await response.text();
            let data: AuthResponse | null = null;
            try {
                data = responseText ? JSON.parse(responseText) : null;
            } catch (parseError) {
                console.error('❌ AuthAPI: Failed to parse login verify response:', parseError);
                if (!response.ok) {
                    const errorMessage = response.status === 429
                        ? 'Too many requests. Please wait a moment and try again.'
                        : `Login verification failed: ${responseText}`;
                    throw createApiError(errorMessage, response.status);
                }
                throw new Error(`Invalid JSON response: ${responseText}`);
            }

            if (!response.ok || !data?.success) {
                if (response.status >= 500) {
                    throw createServerError(response.status);
                }
                const message = data?.message
                    || (response.status === 429 ? 'Too many requests. Please wait a moment and try again.' : 'Login verification failed');
                throw createApiError(message, response.status);
            }

            const jwtToken = data.token || data.idToken;
            if (!jwtToken || !data.user) {
                throw new Error('Invalid response from server');
            }

            console.log('🔐 AuthAPI: Storing token and user data...');
            const user = this.persistLoginSession(data);

            console.log('✅ AuthAPI: Login verification successful', user);
            return user;
        } catch (error: any) {
            console.error('❌ AuthAPI: Login verification error:', error);
            this.clearAuthToken();
            throw error;
        }
    }

    async loginDemo(
        email: string,
        password: string,
        options?: { tenantAbbreviation?: string }
    ): Promise<User> {
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail || !isDemoAccountEmail(normalizedEmail)) {
            throw new Error('Invalid email or password');
        }
        if (!password) {
            throw new Error('Password is required');
        }

        const demoLoginUrl = `${API_BASE_URL}/auth/login/demo`;
        const response = await fetchWithNetworkRetry(demoLoginUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email: normalizedEmail,
                password,
                tenantAbbreviation: options?.tenantAbbreviation,
            }),
        });

        const responseText = await response.text();
        let data: AuthResponse | null = null;
        try {
            data = responseText ? JSON.parse(responseText) : null;
        } catch {
            if (!response.ok) {
                throw createApiError(responseText || 'Demo login failed', response.status);
            }
            throw new Error(`Invalid JSON response: ${responseText}`);
        }

        if (!response.ok || !data?.success) {
            if (response.status >= 500) {
                throw createApiError(
                    formatHttpErrorWithDebug(data?.message || SERVER_ERROR_MESSAGE, demoLoginUrl, {
                        'HTTP status': response.status,
                    }),
                    response.status
                );
            }
            throw createApiError(data?.message || 'Invalid email or password', response.status);
        }

        try {
            return this.persistLoginSession(data);
        } catch (error) {
            this.clearAuthToken();
            throw error;
        }
    }

    async getDemoAccountStatus(): Promise<{
        configured: boolean;
        userExists: boolean;
        email: string;
        notificationEmail: string | null;
    }> {
        const data = await this.makeRequest('/auth/admin/demo-account', { method: 'GET' });
        return {
            configured: Boolean(data?.configured),
            userExists: Boolean(data?.userExists),
            email: typeof data?.email === 'string' ? data.email : DEMO_ACCOUNT_EMAIL,
            notificationEmail:
                typeof data?.notificationEmail === 'string' && data.notificationEmail.trim()
                    ? data.notificationEmail.trim()
                    : null,
        };
    }

    async setDemoAccountPassword(password: string): Promise<void> {
        const data = await this.makeRequest('/auth/admin/demo-account/password', {
            method: 'PUT',
            body: JSON.stringify({ password }),
        });
        if (!data?.success) {
            throw new Error(data?.message || 'Failed to update demo password');
        }
    }

    async setDemoAccountNotificationEmail(notificationEmail: string): Promise<void> {
        const data = await this.makeRequest('/auth/admin/demo-account/notification-email', {
            method: 'PUT',
            body: JSON.stringify({ notificationEmail: notificationEmail.trim() }),
        });
        if (!data?.success) {
            throw new Error(data?.message || 'Failed to update notification email');
        }
    }

    async createAdminUser(payload: {
        email: string;
        firstName: string;
        lastName: string;
        organization?: string;
        active?: boolean;
        role?: UserAccountRole;
    }): Promise<User> {
        const data = await this.makeRequest('/auth/admin/users', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        if (!data?.success || !data?.user) {
            throw new Error(data?.message || 'Failed to create user');
        }
        return data.user as User;
    }

    // Legacy signIn method - now uses two-step flow
    async signIn(email: string): Promise<User> {
        // For backward compatibility, initiate login and throw error to indicate two-step flow
        await this.loginInitiate(email);
        throw new Error('Please check your email for the login link');
    }

    // Initiate registration (step 1: email -> magic link email)
    async registerInitiate(
        email: string,
        options?: {
            source?: string;
            returnPath?: string;
            draft?: { token?: string; data?: Record<string, any>; clientSessionId?: string };
            tenantAbbreviation?: string | null;
        }
    ): Promise<{ success: boolean; message: string }> {
        const registerInitiateUrl = `${API_BASE_URL}/auth/register/initiate`;
        try {
            console.log('🔐 AuthAPI: Initiating registration...');
            const normalizedEmail = email.trim().toLowerCase();
            if (!normalizedEmail) {
                throw new Error('Email is required');
            }

            const response = await fetchWithNetworkRetry(registerInitiateUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: normalizedEmail,
                    source: options?.source,
                    returnPath: options?.returnPath,
                    draft: options?.draft,
                    tenantAbbreviation: options?.tenantAbbreviation || undefined
                }),
            });

            const data: AuthResponse = await response.json();

            if (!response.ok || !data.success) {
                if (response.status >= 500) {
                    throw createApiError(
                        formatHttpErrorWithDebug(data.message || SERVER_ERROR_MESSAGE, registerInitiateUrl, {
                            'HTTP status': response.status,
                        }),
                        response.status
                    );
                }
                const retryAfter = response.status === 429
                    ? (parseInt(response.headers.get('Retry-After') || '60', 10) || 60)
                    : undefined;
                throw createApiError(data.message || 'Registration initiation failed', response.status, retryAfter);
            }

            console.log('✅ AuthAPI: Registration initiation successful');
            return { success: true, message: data.message || 'Registration link sent to your email' };
        } catch (error: any) {
            console.error('❌ AuthAPI: Registration initiation error:', error);
            throw error;
        }
    }

    async registerPrefill(token: string, draftId: string): Promise<{ firstName?: string; lastName?: string }> {
        const params = new URLSearchParams({ token: token.trim(), draftId: draftId.trim() });
        const response = await fetch(`${API_BASE_URL}/auth/register/prefill?${params.toString()}`);
        const text = await response.text();
        if (!response.ok) {
            return {};
        }
        try {
            const data = JSON.parse(text);
            if (!data.success) return {};
            return {
                firstName: typeof data.firstName === 'string' ? data.firstName : undefined,
                lastName: typeof data.lastName === 'string' ? data.lastName : undefined
            };
        } catch {
            return {};
        }
    }

    async registerCheckToken(token: string): Promise<{ valid: boolean; error?: string }> {
        const params = new URLSearchParams({ token: token.trim() });
        const response = await fetch(`${API_BASE_URL}/auth/register/token-status?${params.toString()}`);
        const data = await response.json().catch(() => ({ valid: false, error: 'Invalid response' }));
        return {
            valid: Boolean(data.valid),
            ...(data.error && { error: data.error })
        };
    }

    // Complete registration (step 2: token + profile data -> JWT)
    async registerComplete(token: string, userData: {
        firstName: string;
        lastName: string;
    }): Promise<User> {
        try {
            console.log('🔐 AuthAPI: Completing registration...');
            const trimmedToken = token.trim();
            if (!trimmedToken) {
                throw new Error('Invalid registration token');
            }
            const payload = {
                token: trimmedToken,
                firstName: userData.firstName.trim(),
                lastName: userData.lastName.trim(),
            };
            if (!payload.firstName || !payload.lastName) {
                throw new Error('First and last name are required');
            }

            const response = await fetch(`${API_BASE_URL}/auth/register/complete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const data: AuthResponse = await response.json().catch(() => ({} as AuthResponse));

            if (!response.ok || !data.success) {
                if (response.status >= 500) {
                    throw createServerError(response.status);
                }
                const fallbackMessage = response.status === 400
                    ? 'Token, firstName, and lastName are required'
                    : 'Registration completion failed';
                throw createApiError(data.message || fallbackMessage, response.status);
            }

            const jwtToken = data.token || data.idToken;
            if (!jwtToken || !data.user) {
                throw new Error('Invalid response from server');
            }

            // Store token and user data
            console.log('🔐 AuthAPI: Storing token and user data...');
            this.setAuthToken(jwtToken);
            localStorage.setItem('user', JSON.stringify(data.user));

            console.log('✅ AuthAPI: Registration completion successful', data.user);
            return data.user;
        } catch (error: any) {
            console.error('❌ AuthAPI: Registration completion error:', error);
            this.clearAuthToken();
            throw error;
        }
    }

    // Legacy signUp method - now uses two-step flow
    async signUp(_email: string, _userData: {
        firstName: string;
        lastName: string;
        organization: string;
    }): Promise<User> {
        // For backward compatibility, initiate registration and throw error to indicate two-step flow
        await this.registerInitiate(_email);
        throw new Error('Please check your email for the registration link');
    }

    signOut = async (): Promise<void> => {
        try {
            this.clearAuthToken();
            invalidateAllCachesUsedByFetchMeta();
        } catch (error) {
            console.error('Error during sign out:', error);
            // Ensure cleanup happens even if there's an error
            if (this.isBrowserEnvironment()) {
                localStorage.removeItem('idToken');
                localStorage.removeItem('user');
                sessionStorage.removeItem('authToken');
                sessionStorage.removeItem('idToken');
                clearImpersonationStorage();
            }
            clearItemsCache();
            clearRelationLabelCache();
            invalidateAllCachesUsedByFetchMeta();
        }
    }



    async getCurrentUser(): Promise<User | null> {
        try {
            const token = this.getAuthToken();
            if (!token) {
                console.log('🔐 AuthAPI: No token found, cannot get current user');
                return null;
            }

            // First try to get from localStorage
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                try {
                    const user = JSON.parse(storedUser);
                    console.log('✅ AuthAPI: Got user from localStorage');
                    // Still verify with backend to ensure token is valid
                    // Don't clear token on 401 here - we'll check the error and decide
                    try {
                        const response = await this.makeRequest('/auth/user', {}, false); // Don't auto-clear on 401
                        if (response.user) {
                            localStorage.setItem('user', JSON.stringify(response.user));
                            return response.user;
                        }
                    } catch (backendError) {
                        // Backend verification failed, but we have a stored user
                        // Only clear if it's a 401 (unauthorized), otherwise return stored user
                        if (backendError instanceof Error && backendError.message.includes('401')) {
                            console.log('❌ AuthAPI: Token invalid (401), clearing auth');
                            console.trace('🔍 AuthAPI: Token cleared due to 401 in getCurrentUser');
                            this.clearAuthToken();
                            triggerAuthError();
                            return null;
                        }
                        // For other errors, return stored user as fallback
                        console.log('⚠️ AuthAPI: Backend verification failed, using stored user');
                        return user;
                    }
                    return user;
                } catch (e) {
                    // Invalid JSON, continue to fetch from backend
                    console.log('⚠️ AuthAPI: Invalid user JSON in localStorage, fetching from backend');
                }
            }

            // Fetch from backend if not in localStorage
            console.log('🔐 AuthAPI: Fetching user from backend...');
            // Use /auth/verify endpoint to verify token and get user
            const response = await this.makeRequest('/auth/verify', {
                method: 'POST',
            });
            if (response.user) {
                localStorage.setItem('user', JSON.stringify(response.user));
                console.log('✅ AuthAPI: Got user from backend');
                return response.user;
            }
            return null;
        } catch (error: any) {
            console.error('❌ AuthAPI: Error getting current user:', error);
            // Only clear token if it's explicitly an authentication error
            // Don't clear on network errors, timeouts, or other non-auth errors
            const errorMessage = error?.message || error?.toString() || '';
            const isAuthError = errorMessage.includes('401') ||
                errorMessage.includes('Authentication required') ||
                errorMessage.includes('unauthorized') ||
                (error instanceof Error && error.message.includes('Authentication'));

            if (isAuthError) {
                console.log('❌ AuthAPI: Authentication error detected, clearing token');
                this.clearAuthToken();
                triggerAuthError();
            } else {
                console.log('⚠️ AuthAPI: Non-auth error getting user, keeping token:', errorMessage);
            }
            return null;
        }
    }

    async checkAuthStatus(): Promise<{ authenticated: boolean; user: User | null }> {
        try {
            const token = this.getAuthToken();
            if (!token) {
                return {
                    authenticated: false,
                    user: null,
                };
            }

            const user = await this.getCurrentUser();
            return {
                authenticated: !!user,
                user,
            };
        } catch (error) {
            console.error('Error checking auth status:', error);
            return {
                authenticated: false,
                user: null,
            };
        }
    }

    async updateUser(userData: Partial<User['info']> & {
        userType?: 'submitter' | 'website_owner';
        uiSettings?: {
            hideInbox?: boolean;
            hideActions?: boolean;
            hideSubmissions?: boolean;
        };
    }): Promise<User> {
        const response = await this.makeRequest('/auth/user', {
            method: 'PUT',
            body: JSON.stringify(userData),
        });
        if (response.user) {
            localStorage.setItem('user', JSON.stringify(response.user));
        }
        return response.user;
    }

    /** ACL groups the current user belongs to (uid + title), for the file-sharing prompt. */
    async getMyGroups(): Promise<Array<{ uid: string; title: string }>> {
        try {
            const response = await this.makeRequest('/auth/my-groups', {}, false);
            return Array.isArray(response?.groups) ? response.groups : [];
        } catch (error) {
            console.error('Error fetching current user groups:', error);
            return [];
        }
    }

    /** Users visible to the caller (peers, or all active users for admins): uid + display name. */
    async getPeers(): Promise<Array<{ uid: string; name: string }>> {
        try {
            const response = await this.makeRequest('/users/peers', {}, false);
            const list: any[] = Array.isArray(response?.peers)
                ? response.peers
                : (Array.isArray(response) ? response : []);
            return list
                .map((u) => {
                    const uid = u.id || u.uid || u._id || '';
                    const composed = [u.firstName || u.info?.firstName, u.lastName || u.info?.lastName]
                        .filter(Boolean)
                        .join(' ')
                        .trim();
                    const name = u.name || u.info?.name || composed || u.email || uid;
                    return { uid, name };
                })
                .filter((u) => u.uid);
        } catch (error) {
            console.error('Error fetching peers:', error);
            return [];
        }
    }

    /** Save or clear one table's column order and hidden columns on the signed-in account. */
    async saveColumnView(body: {
        tableKey: string;
        order?: string[];
        hidden?: string[];
        clear?: boolean;
    }): Promise<void> {
        await this.makeRequest('/auth/user/column-view', {
            method: 'PUT',
            body: JSON.stringify(body),
        });
    }

    /** Persist the user's remembered file-sharing defaults on their account. */
    async updateFileSharingDefaults(defaults: {
        fileVisibilityDefault?: 'private' | 'shared';
        fileShareGroupDefault?: string | null;
        filePublicDefault?: boolean;
    }): Promise<User | null> {
        const response = await this.makeRequest('/auth/user', {
            method: 'PUT',
            body: JSON.stringify(defaults),
        });
        if (response?.user) {
            localStorage.setItem('user', JSON.stringify(response.user));
        }
        return response?.user ?? null;
    }

    async getUserById(uid: string): Promise<User | null> {
        try {
            const response = await this.makeRequest(`/auth/user/${uid}`);
            return response.user;
        } catch (error) {
            console.error('Error getting user by ID:', error);
            return null;
        }
    }

    async deleteAccount(): Promise<void> {
        await this.makeRequest('/auth/me', {
            method: 'DELETE',
        });
        this.clearAuthToken();
    }

    // Utility method to get ID token from localStorage
    getIdToken(): string | null {
        return this.getAuthToken();
    }

    /**
     * Store a JWT and optional user (e.g. from impersonation) and persist to storage.
     * Caller should then redirect (e.g. window.location.href = '/') to reload the app as that user.
     */
    loginWithToken(token: string, user?: User | null): void {
        const trimmed = token?.trim();
        if (!trimmed) {
            throw new Error('Token is required');
        }
        this.setAuthToken(trimmed);
        if (user && this.isBrowserEnvironment()) {
            localStorage.setItem('user', JSON.stringify(user));
        }
    }
}

export const authAPI = new AuthAPI();
export default authAPI;
